/**
 * IconButton component for icon-only buttons.
 *
 * Optimized for circular or square icon buttons common in toolbars,
 * modals, and action menus. Uses CVA for type-safe variants.
 */

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Spinner } from '@/design-system/atoms/Spinner'

/**
 * IconButton variant definitions using CVA.
 */
const iconButtonVariants = cva(
  // Base styles - common to all icon buttons
  'inline-flex items-center justify-center font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Default: Subtle gray background
        default:
          'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-700 focus-visible:ring-gray-400 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
        // Primary: Brand color
        primary:
          'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500',
        // Secondary: Outlined
        secondary:
          'bg-transparent text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-700 focus-visible:ring-gray-400 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200',
        // Ghost: No background, minimal styling
        ghost:
          'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-gray-400 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200',
        // Danger: Red for destructive actions
        danger:
          'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        // Success: Green for positive actions
        success:
          'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',
        // Dark: Dark background (common in chat inputs)
        dark: 'bg-gray-900 text-white hover:bg-gray-800 focus-visible:ring-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600',
        // Brand: Brand hover color (no background)
        brand:
          'bg-transparent text-gray-500 hover:text-brand-600 hover:bg-gray-100 focus-visible:ring-brand-400 dark:text-gray-400 dark:hover:text-brand-400 dark:hover:bg-gray-700',
      },
      size: {
        xs: 'h-7 w-7 text-xs',
        sm: 'h-8 w-8 text-sm',
        md: 'h-9.5 w-9.5 text-base',
        lg: 'h-11 w-11 text-lg',
        xl: 'h-12 w-12 text-xl',
      },
      shape: {
        square: 'rounded-lg',
        rounded: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      shape: 'rounded',
    },
  }
)

/**
 * IconButton component props.
 */
export interface IconButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /** Shows loading spinner */
  loading?: boolean
  /** Icon to display (ReactNode for flexibility) */
  icon?: React.ReactNode
  /** Children (can be used instead of icon prop) */
  children?: React.ReactNode
}

/**
 * IconButton component for icon-only actions.
 *
 * Features:
 * - Optimized for single icons
 * - Multiple variants (default, primary, ghost, etc)
 * - Size variants (xs to xl)
 * - Shape options (square, rounded/circular)
 * - Loading state with spinner
 * - Full accessibility support
 *
 * @example
 * ```tsx
 * // Close button
 * <IconButton
 *   variant="default"
 *   onClick={onClose}
 *   aria-label="Close"
 * >
 *   <XIcon className="h-5 w-5" />
 * </IconButton>
 *
 * // Send button
 * <IconButton
 *   variant="dark"
 *   shape="square"
 *   size="sm"
 *   loading={sending}
 * >
 *   <PaperPlaneIcon className="h-4 w-4" />
 * </IconButton>
 * ```
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      loading,
      icon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const content = icon || children

    return (
      <button
        ref={ref}
        className={cn(iconButtonVariants({ variant, size, shape, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Spinner className="h-4 w-4" size="sm" /> : content}
      </button>
    )
  }
)

IconButton.displayName = 'IconButton'

// Default export for convenience
export default IconButton

// Export variants for reuse
export { iconButtonVariants }
